import { useQuery } from '@tanstack/react-query';

import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';
import type { Period } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';

const log = createLogger('usePeriodListAllQuery');

export const QUERY_KEY = 'periodListAll';

export type ListAllPeriodsResponse = {
  periods: Period[];
};

export function usePeriodListAllQuery({
  includeStudents = true,
  includeArchived = false,
}: { includeStudents?: boolean; includeArchived?: boolean } = {}) {
  const httpClient = useHttpClient();

  const { data, error, status } = useQuery<ListAllPeriodsResponse>({
    queryKey: [QUERY_KEY, includeStudents, includeArchived],
    queryFn: async () => {
      log.debug('Fetching all periods');

      const queryString = `/period/listAll?includeStudents=${includeStudents}&includeArchived=${includeArchived}`;
      const response = await httpClient.get<ListAllPeriodsResponse>(queryString);
      log.info('Fetched periods successfully', {
        periodCount: response.periods.length,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch all periods', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status };
}
